/**
 * TrafficSourceTable Component
 * Displays traffic sources with detailed metrics
 */
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";
import { formatNumber, formatPercentage } from "../../../utils/formatters";
import { TrafficSourceData } from "../types";

interface TrafficSourceTableProps {
  data: TrafficSourceData;
  showTotals?: boolean;
  titleKey?: string;
}

export default function TrafficSourceTable({
  data,
  showTotals = true,
  titleKey = "reports.trafficByChannel",
}: TrafficSourceTableProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);

  return (
    <div
      className={`rounded-3xl p-4 sm:p-6 border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <h3
        className={`text-base sm:text-lg font-bold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}
      >
        {t(titleKey)}
      </h3>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden">
            <table className="min-w-full w-full">
              <thead>
                <tr
                  className={`border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.source")}
                  </th>
                  <th
                    className={`text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.sessions_col")}
                  </th>
                  <th
                    className={`text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.users_col")}
                  </th>
                  <th
                    className={`text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.conversions")}
                  </th>
                  <th
                    className={`text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.cvr")}
                  </th>
                  <th
                    className={`text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.newUserRate")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.sources.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-b transition-colors ${
                      darkMode
                        ? "border-gray-700 hover:bg-gray-700/50"
                        : "border-gray-100 hover:bg-gray-50"
                    }`}
                  >
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${darkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {row.default_channel_group}
                    </td>
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {formatNumber(row.sessions)}
                    </td>
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {formatNumber(row.new_users)}
                    </td>
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {formatNumber(row.conversions)}
                    </td>
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {formatPercentage(row.cvr)}
                    </td>
                    <td
                      className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {formatPercentage(row.new_user_rate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {showTotals && data.total && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div
            className={`rounded-2xl p-4 border ${
              darkMode
                ? "bg-gradient-to-br from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
                : "bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
            }`}
          >
            <p
              className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {t("reports.totalSessions")}
            </p>
            <p
              className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {formatNumber(data.total.sessions)}
            </p>
          </div>

          <div
            className={`rounded-2xl p-4 border ${
              darkMode
                ? "bg-gradient-to-br from-[var(--brand-secondary)]/20 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/30"
                : "bg-gradient-to-br from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/20"
            }`}
          >
            <p
              className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {t("reports.totalConversions")}
            </p>
            <p
              className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {formatNumber(data.total.conversions)}
            </p>
          </div>

          <div
            className={`rounded-2xl p-4 border ${
              darkMode
                ? "bg-gradient-to-br from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
                : "bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
            }`}
          >
            <p
              className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {t("reports.totalCVR")}
            </p>
            <p
              className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {formatPercentage(data.total.cvr)}
            </p>
          </div>

          <div
            className={`rounded-2xl p-4 border ${
              darkMode
                ? "bg-gradient-to-br from-[var(--brand-secondary)]/20 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/30"
                : "bg-gradient-to-br from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/20"
            }`}
          >
            <p
              className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {t("reports.avgNewUserRate")}
            </p>
            <p
              className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {formatPercentage(data.total.new_user_rate)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
